import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Card,
  Collapse,
  IconButton,
  TableCell,
  TableRow,
  Table,
  TableHead,
  TableContainer,
  TableBody,
  Paper,
  Box,
  CircularProgress,
  TablePagination,
  Button,
  TextField,
} from '@mui/material';
import ArgonBox from 'components/ArgonBox';
import ArgonTypography from 'components/ArgonTypography';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import companyInfo from 'context/companyInfo';
import { UserContext } from 'context/userContext';
import { getAllProductsFromDb } from 'services/product';
import ProductExpandableTable from 'layouts/products/components/ProductExpandableTable/';
import Icon from '@mui/material/Icon';
import { grey } from '@mui/material/colors';
import OfferingDetailsView from './components/OfferingDetailsView';
import { OrgContext } from 'context/orgContext'; // Import OrgContext
import { userOrgs } from 'services/org'; // Import userOrgs

function Offering() {
  const { userId } = useContext(UserContext);
  const { orgs, setOrgs } = useContext(OrgContext); // Use OrgContext
  const [productsData, setProductsData] = useState([]);
  const [expandedRow, setExpandedRow] = useState(null);
  const [topLevelPage, setTopLevelPage] = useState(0);
  const [topLevelRowsPerPage, setTopLevelRowsPerPage] = useState(8);
  const [innerPage, setInnerPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDetailsView, setShowDetailsView] = useState(false);
  const [selectedDetailProduct, setSelectedDetailProduct] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!userId) return;
      setLoading(true);
      try {
        // Fetch both products and orgs in parallel
        const [productsResponse, orgsResponse] = await Promise.all([getAllProductsFromDb(), userOrgs()]);

        const allProducts = productsResponse?.products || [];
        const allOrgs = orgsResponse?.orgs || [];
        setOrgs(allOrgs);

        // Create a map for quick org lookup
        const orgsMap = new Map(allOrgs.map((org) => [org.org_id, org]));

        // --- THIS IS THE KEY FILTERING LOGIC ---
        // 1. Filter groups to only include those that have at least one product with productOptions.
        const offeringGroups = allProducts.filter((group) =>
          group.products.some((p) => p.related_data?.productOptions?.length > 0 || p.related_data.features?.length > 0)
        );

        // 2. Attach the full org object to each group.
        const offeringsWithOrgData = offeringGroups.map((group) => {
          // Find the org for this group using the org_id from the first product
          const org = orgsMap.get(group.products[0]?.org_id);
          return { ...group, org };
        });

        setProductsData(offeringsWithOrgData);
      } catch (error) {
        console.error('Failed to fetch offering data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [userId, setOrgs]);

  const filteredProductsData = useMemo(() => {
    if (!searchTerm) return productsData;
    const lowerCaseSearchTerm = searchTerm.toLowerCase();
    return productsData.filter((group) => group._id.name.toLowerCase().includes(lowerCaseSearchTerm));
  }, [productsData, searchTerm]);

  const handleRowClick = (rowId) => {
    setExpandedRow((prev) => (prev === rowId ? null : rowId));
    if (expandedRow !== rowId) setInnerPage(0);
  };

  const handleTopLevelPageChange = (event, newPage) => setTopLevelPage(newPage);
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setTopLevelPage(0);
  };

  const columns = [
    { name: 'name', align: 'left' },
    { name: 'orgSource', align: 'left' },

    { name: 'actions', align: 'right', width: '50px' },
  ];

  const startIndex = topLevelPage * topLevelRowsPerPage;
  const endIndex = startIndex + topLevelRowsPerPage;
  const displayedGroups = filteredProductsData.slice(startIndex, endIndex);

  const rows = useMemo(() => {
    return displayedGroups?.map((group) => ({
      id: group._id.external_id,
      name: (
        <ArgonTypography variant="caption" color="secondary" fontWeight="bold">
          {`${group._id.name} (${group.products.length})`}
        </ArgonTypography>
      ),
      orgSource: (
        <ArgonTypography fontWeight="medium" variant="caption" color="secondary">
          {group.org?.name || 'N/A'}
        </ArgonTypography>
      ),
      onClick: () => handleRowClick(group._id.external_id),
      selected: expandedRow === group._id.external_id,
      actions: (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            handleRowClick(group._id.external_id);
          }}
        >
          <Icon>{expandedRow === group._id.external_id ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</Icon>
        </IconButton>
      ),
    }));
  }, [displayedGroups, expandedRow]);

  const handleBackClick = () => {
    setShowDetailsView(false);
    setSelectedDetailProduct(null);
  };

  const handleViewDetails = (product) => {
    setSelectedDetailProduct(product);
    setShowDetailsView(true);
  };

  const renderRowSubComponent = ({ row }) => {
    const selectedGroup = productsData.find((group) => group._id.external_id === row.id);
    if (!selectedGroup) return null;

    // The inner table will now show ALL product versions in the group.
    const tableRows = selectedGroup.products.map((product) => ({
      productName: (
        <ArgonTypography fontWeight="medium" variant="body2" color="secondary">
          {product.name || 'N/A'}
        </ArgonTypography>
      ),
      createdDate: (
        <ArgonTypography fontWeight="medium" variant="body2" color="secondary">
          {new Date(product.created_date).toLocaleString()}
        </ArgonTypography>
      ),
      createdBy: (
        <ArgonTypography fontWeight="medium" variant="body2" color="secondary">
          {product.user?.email || 'N/A'}
        </ArgonTypography>
      ),
      product_version: (
        <ArgonTypography fontWeight="medium" variant="body2" color="secondary">
          {product.product_version || '-'}
        </ArgonTypography>
      ),
      actions: (
        <Button variant="contained" color="info" size="small" onClick={() => handleViewDetails(product)}>
          View Details
        </Button>
      ),
      id: product._id,
    }));

    const subColumns = [
      { name: 'productName', label: 'Offering Name', align: 'left', component: (r) => r.productName },
      { name: 'createdDate', label: 'Created Date', align: 'left', component: (r) => r.createdDate },
      { name: 'createdBy', label: 'Created By', align: 'left', component: (r) => r.createdBy },
      { name: 'product_version', label: 'Version', align: 'left', component: (r) => r.product_version },
      { name: 'actions', label: 'Actions', align: 'center', component: (r) => r.actions },
    ];

    // --- THIS IS THE FIX ---
    // We no longer need inner pagination, so the slicing logic is removed.
    // The `displayedInnerRows` variable is now just `tableRows`.

    return (
      <TableRow>
        <TableCell colSpan={columns.length} style={{ padding: 0 }}>
          <Collapse in={expandedRow === row.id} timeout="auto" unmountOnExit>
            <ArgonBox p={3} bgcolor={grey[50]}>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead sx={{ display: 'contents' }}>
                    <TableRow>
                      {subColumns.map((column) => (
                        <TableCell key={column.name} align={column.align}>
                          {column.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tableRows.map(
                      (
                        innerRow // Use tableRows directly
                      ) => (
                        <TableRow key={innerRow.id}>
                          {subColumns.map((column) => (
                            <TableCell key={column.name} align={column.align}>
                              {column.component(innerRow)}
                            </TableCell>
                          ))}
                        </TableRow>
                      )
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              {/* The inner TablePagination component is also removed */}
            </ArgonBox>
          </Collapse>
        </TableCell>
      </TableRow>
    );
  };
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <Card>
          <ArgonBox display="flex" justifyContent="space-between" alignItems="center" p={3}>
            <ArgonTypography variant="h6">{showDetailsView ? selectedDetailProduct.name : 'Offerings'}</ArgonTypography>
            <Box>
              {!showDetailsView && (
                <TextField
                  placeholder="Search Offerings"
                  size="small"
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
              )}
              {showDetailsView && (
                <Button onClick={handleBackClick} variant="contained" color="info">
                  Back
                </Button>
              )}
            </Box>
          </ArgonBox>
          {loading ? (
            <Box display="flex" justifyContent="center" p={5}>
              <CircularProgress />
            </Box>
          ) : showDetailsView ? (
            <OfferingDetailsView product={selectedDetailProduct} onClose={handleBackClick} />
          ) : (
            <>
              <ProductExpandableTable columns={columns} rows={rows} renderRowSubComponent={renderRowSubComponent} />
              <TablePagination
                rowsPerPageOptions={[topLevelRowsPerPage]}
                component="div"
                count={filteredProductsData.length}
                rowsPerPage={topLevelRowsPerPage}
                page={topLevelPage}
                onPageChange={handleTopLevelPageChange}
              />
            </>
          )}
        </Card>
      </ArgonBox>
      <Footer company={companyInfo} />
    </DashboardLayout>
  );
}

export default Offering;
